import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IContent } from '@/@types/cms';
import { pageFactory } from './pages';
import { ErrorBoundary } from 'react-error-boundary';
import { componentFactory } from './components';
import { datadogRum } from '@/lib/DatadogService';
import ComponentLazyShimmer from './ComponentLazyShimmer';
import PageLoading from '@/routes/PageLoading';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GlobalErrorPage from '../components/pages/GlobalErrorPage';
import { selectPage } from '@/redux/selectors/pageSelectors';
import ContentArea from '@/components/cms/ContentArea';
import { isDynamicImportLoadError, recoverFromAssetLoadError } from '@/utils/assetReload';
import { getPageLoadingVariant } from '@/routes/pageLoadingVariants';
import { RootState } from '@/redux/store';

interface IComponentFactoryProps {
  components?: Array<IContent>;
  componentsProps?: Record<string, unknown>;
  renderPages?: boolean;
}

export interface IComponentProps extends IContent {
  [key: string]: unknown;
  isFirstItem?: boolean;
  isLastItem?: boolean;
  index?: number;
  fallbackBlock?: IContent[];
}

const logMissingComponentError = (_type: string, componentType: 'Page' | 'Component') => {
  datadogRum.addAction(`Missing ${componentType} view: ${_type ?? 'UNDEFINED'}`);
};

export const Page: React.FC<IComponentProps> = React.memo(({ contentType, ...props }) => {
  const pageType = `${contentType?.slice(-1)[0]}`;
  const PageComponent = pageFactory(pageType);
  const navigate = useNavigate();
  const pages = useSelector(selectPage);
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.token);
  const loadingVariant = getPageLoadingVariant({
    contentType: pageType,
    isAuthenticated,
    pathname: window.location.pathname,
    search: window.location.search
  });

  const sanitizeToPath = useCallback((value: string): string => {
    try {
      const url = new URL(value);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return value.startsWith('/') ? value : `/${value}`;
    }
  }, []);

  useEffect(() => {
    if (!PageComponent) {
      logMissingComponentError(pageType, 'Page');
      if (window.location.pathname != '/') {
        window.location.assign('/');
      }
    }
  }, [PageComponent, pageType]);

  if (!PageComponent) {
    return null;
  }
  return (
    <ErrorBoundary
      fallback={
        pages?.pageLinks?.GlobalError ? null : (
          <GlobalErrorPage {...props} renderBackupContent={true} />
        )
      }
      onError={error => {
        if (
          isDynamicImportLoadError(error) &&
          recoverFromAssetLoadError(`Page lazy import failed: ${pageType}`)
        ) {
          return;
        }

        datadogRum.addError(error);
        if (pages?.pageLinks?.GlobalError) navigate(sanitizeToPath(pages?.pageLinks?.GlobalError));
      }}
    >
      <Suspense
        fallback={
          <PageLoading
            {...({ variant: loadingVariant } as React.ComponentProps<typeof PageLoading>)}
          />
        }
      >
        <PageComponent {...props} contentType={contentType} />
      </Suspense>
    </ErrorBoundary>
  );
});

const componentShimmerBlacklist = ['navigation', 'footer'];
// Give lazy PDP sections a short fallback window on browsers without IntersectionObserver,
// and start rendering slightly before they enter the viewport to avoid visible pop-in.
const LAZY_RENDER_FALLBACK_DELAY_MS = 4000;
const LAZY_RENDER_ROOT_MARGIN = '600px 0px';
const lazyPDPComponentTypes = new Set([
  'IPDPProgramSegmentsLayoutBlock',
  'IPDPProgramCreditLayoutBlock',
  'IPDPTestimonialsLayoutBlock',
  'IPDPTopicsAndIndustriesLayoutBlock',
  'IPDPProgramFacultyLayoutBlock',
  'KeyTakewayLayoutBlock',
  'GenericVideoBlock',
  'PodcastListingBlock',
  'PDPFeaturedPublicationLayoutBlock'
]);

const LazyPDPComponent = ({
  componentType,
  render
}: {
  componentType: string;
  render: () => React.ReactNode;
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const placeholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldRender) return;

    const placeholder = placeholderRef.current;
    if (!placeholder || !('IntersectionObserver' in window)) {
      const timeoutId = window.setTimeout(
        () => setShouldRender(true),
        LAZY_RENDER_FALLBACK_DELAY_MS
      );
      return () => window.clearTimeout(timeoutId);
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: LAZY_RENDER_ROOT_MARGIN }
    );

    observer.observe(placeholder);
    return () => observer.disconnect();
  }, [shouldRender]);

  if (shouldRender) return <>{render()}</>;

  return <div ref={placeholderRef} data-lazy-pdp-component={componentType} />;
};

export const Component: React.FC<IComponentProps> = React.memo(({ contentType, ...props }) => {
  const componentType = contentType?.slice(-1)[0] ?? '';
  const showFallback = !componentShimmerBlacklist.some(
    a => componentType?.toLowerCase()?.indexOf(a) >= 0
  );
  const SingleComponent = useMemo(() => componentFactory(`${componentType}`), [componentType]);

  const renderComponent = useCallback(() => {
    if (!SingleComponent) {
      logMissingComponentError(componentType, 'Component');
      return null;
    }

    return (
      <ErrorBoundary
        fallback={
          (props?.fallbackBlock?.length ?? 0) > 0 ? (
            <ContentArea components={props.fallbackBlock} />
          ) : (
            <></>
          )
        }
        onError={(error, info) => {
          if (
            isDynamicImportLoadError(error) &&
            recoverFromAssetLoadError(`Component lazy import failed: ${componentType}`)
          ) {
            return;
          }

          console.error(error, info);
        }}
      >
        <Suspense
          fallback={
            showFallback ? (
              <ComponentLazyShimmer
                componentType={componentType}
                creditInfoLoadingLayout={props.creditInfoLoadingLayout === true}
              />
            ) : (
              <></>
            )
          }
        >
          <SingleComponent {...props} contentType={contentType} />
        </Suspense>
      </ErrorBoundary>
    );
  }, [SingleComponent, componentType, contentType, props, showFallback]);

  if (props.isPDP && !props.isFirstItem && lazyPDPComponentTypes.has(componentType)) {
    return <LazyPDPComponent componentType={componentType} render={renderComponent} />;
  }

  return renderComponent();
});

const ComponentFactory: React.FC<IComponentFactoryProps> = React.memo(
  ({ components, componentsProps, renderPages }) => (
    <>
      {components?.map((component, index) => {
        const ContentComponent =
          renderPages && component.contentType?.includes('Page') ? Page : Component;

        return (
          <ContentComponent
            key={index}
            {...component}
            {...componentsProps}
            isFirstItem={index === 0}
            index={index}
            isLastItem={index === components.length - 1}
          />
        );
      })}
    </>
  )
);

export default ComponentFactory;
